import { QueryClient } from "@tanstack/react-query"
import { ApiError } from "./apiClient"

/**
 * Shared TanStack Query client for the Portal.
 *
 * Defaults chosen to match the behaviour the hand-rolled apiCache gave us,
 * without surprising the operator:
 *  - staleTime 30s: matches the old default GET TTL, so quick navigations
 *    reuse data instead of refetching on every mount.
 *  - gcTime 5min: keep unused data around briefly for back/forward nav.
 *  - refetchOnWindowFocus off: ops users tab away constantly; we don't want a
 *    refetch storm every time they come back. Explicit refetch / invalidation
 *    on mutation is the model instead.
 *  - retry once: apiClient already handles 401 refresh + timeouts; a single
 *    retry covers a transient blip without hammering a failing endpoint.
 *
 * Per-query overrides (e.g. longer staleTime for the slow-moving item/supplier
 * catalogues, matching the old 30-min TTL rules) are set at the useQuery call
 * site via the query hooks in src/queries.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // Retry transient failures once, but never 4xx client errors — those
        // are deterministic (bad request / forbidden / not found), so a retry
        // just wastes a round-trip and delays the error UI.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            return false
          }
          return failureCount < 1
        },
      },
    },
  })
}

/** Catalogue endpoints (items/suppliers) change rarely — reuse the old 30-min TTL. */
export const CATALOGUE_STALE_TIME = 30 * 60_000
